import { FormEvent, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  Bell,
  Bus,
  CalendarDays,
  CheckCircle2,
  Clock,
  CreditCard,
  Download,
  Heart,
  Loader2,
  LogOut,
  Mail,
  MapPin,
  Phone,
  Save,
  Settings,
  ShieldCheck,
  Ticket,
  User,
  X,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { AuthService } from '../../services/auth';
import { ClientBookingService } from '../../services/clientBookingService';
import { RouteService } from '../../services/routeService';
import { ScheduleService } from '../../services/scheduleService';
import { Booking, BusSchedule, Route } from '../../types';

type DashboardTab = 'profile' | 'bookings' | 'book' | 'saved' | 'settings';

const tabs: Array<{ id: DashboardTab; label: string; icon: typeof User }> = [
  { id: 'bookings', label: 'My Bookings', icon: Ticket },
  { id: 'book', label: 'Book a Trip', icon: Bus },
  { id: 'saved', label: 'Saved Routes', icon: Heart },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const statusClass = (status: string) => {
  switch (status) {
    case 'confirmed':
    case 'paid':
      return 'bg-success-50 text-success-700 border-success-200';
    case 'pending':
      return 'bg-warning-50 text-warning-700 border-warning-200';
    case 'cancelled':
    case 'failed':
      return 'bg-danger-50 text-danger-700 border-danger-200';
    default:
      return 'bg-secondary-50 text-secondary-700 border-secondary-200';
  }
};

const formatDate = (date?: Date) =>
  date
    ? date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : 'Not set';

const formatMoney = (amount?: number) => `GHS ${(amount || 0).toFixed(2)}`;

export function ClientDashboard() {
  const { user, signOut, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as DashboardTab) || 'profile';
  const [activeTab, setActiveTab] = useState<DashboardTab>(tabs.some((tab) => tab.id === initialTab) ? initialTab : 'profile');
  const [routes, setRoutes] = useState<Route[]>([]);
  const [schedules, setSchedules] = useState<BusSchedule[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [savedRouteIds, setSavedRouteIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [creatingBooking, setCreatingBooking] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [initializingPayment, setInitializingPayment] = useState(false);

  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phoneNumber: user?.phoneNumber || '',
  });
  const [prefs, setPrefs] = useState({
    email: user?.notificationPrefs?.email ?? true,
    sms: user?.notificationPrefs?.sms ?? false,
    push: user?.notificationPrefs?.push ?? true,
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [bookingForm, setBookingForm] = useState({
    routeId: '',
    travelDate: '',
    passengerName: '',
    passengerPhone: '',
    passengerEmail: '',
    seatNumber: '',
    notes: '',
  });

  useEffect(() => {
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    setProfileForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.phoneNumber || '',
    });
    setPrefs({
      email: user?.notificationPrefs?.email ?? true,
      sms: user?.notificationPrefs?.sms ?? false,
      push: user?.notificationPrefs?.push ?? true,
    });
    setBookingForm((current) => ({
      ...current,
      passengerName: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
      passengerPhone: user?.phoneNumber || '',
      passengerEmail: user?.email || '',
    }));
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;

    const storageKey = `wote:savedRoutes:${user.id}`;
    const storedRoutes = window.localStorage.getItem(storageKey);
    if (storedRoutes) {
      setSavedRouteIds(JSON.parse(storedRoutes));
    }

    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const [activeRoutes, activeSchedules, clientBookings] = await Promise.all([
          RouteService.getActiveRoutes(),
          ScheduleService.getSchedules(),
          ClientBookingService.getBookingsForUser(user.id),
        ]);

        setRoutes(activeRoutes);
        setSchedules(
          activeSchedules.filter((schedule) => schedule.status === 'scheduled' || schedule.status === 'active')
        );
        setBookings(clientBookings);
      } catch (error: any) {
        console.error('Client dashboard load error:', error);
        toast.error(error.message || 'Failed to load your account');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    window.localStorage.setItem(`wote:savedRoutes:${user.id}`, JSON.stringify(savedRouteIds));
  }, [savedRouteIds, user?.id]);

  const selectedRoute = useMemo(
    () => routes.find((route) => route.id === bookingForm.routeId),
    [bookingForm.routeId, routes]
  );

  const routeSchedules = useMemo(() => {
    const now = new Date();
    return schedules
      .filter((schedule) => schedule.routeId === bookingForm.routeId && schedule.departureTime >= now)
      .sort((a, b) => a.departureTime.getTime() - b.departureTime.getTime());
  }, [bookingForm.routeId, schedules]);

  const savedRoutes = useMemo(
    () => routes.filter((route) => savedRouteIds.includes(route.id)),
    [routes, savedRouteIds]
  );

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Client User';

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const handleProfileSave = async (event: FormEvent) => {
    event.preventDefault();
    setSavingProfile(true);
    try {
      const result = await AuthService.updateProfile(profileForm);
      if (!result.success) throw new Error(result.error || 'Failed to save profile');
      await refreshUser();
      toast.success('Profile updated');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePrefsSave = async () => {
    setSavingPrefs(true);
    try {
      const result = await AuthService.updateNotificationPrefs(prefs);
      if (!result.success) throw new Error(result.error || 'Failed to save settings');
      await refreshUser();
      toast.success('Notification settings updated');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handlePasswordSave = async (event: FormEvent) => {
    event.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setSavingPassword(true);
    try {
      const result = await AuthService.updatePassword(passwordForm.currentPassword, passwordForm.newPassword);
      if (!result.success) throw new Error(result.error || 'Failed to update password');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password updated');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleRouteSelect = (routeId: string) => {
    const nextSchedule = schedules
      .filter((schedule) => schedule.routeId === routeId && schedule.departureTime >= new Date())
      .sort((a, b) => a.departureTime.getTime() - b.departureTime.getTime())[0];

    setBookingForm((current) => ({
      ...current,
      routeId,
      travelDate: nextSchedule
        ? nextSchedule.departureTime.toISOString().slice(0, 10)
        : current.travelDate,
    }));
  };

  const handleCreateBooking = async (event: FormEvent) => {
    event.preventDefault();
    if (!user?.id || !selectedRoute) {
      toast.error('Choose a route before booking');
      return;
    }
    if (!bookingForm.travelDate) {
      toast.error('Choose a travel date');
      return;
    }

    const travelDate = new Date(bookingForm.travelDate);
    const matchingSchedule = routeSchedules.find(
      (schedule) => schedule.departureTime.toISOString().slice(0, 10) === bookingForm.travelDate
    );

    if (matchingSchedule) {
      travelDate.setHours(matchingSchedule.departureTime.getHours(), matchingSchedule.departureTime.getMinutes(), 0, 0);
    } else if (selectedRoute.departureTime) {
      const [hour, minute] = selectedRoute.departureTime.split(':').map(Number);
      travelDate.setHours(hour || 0, minute || 0, 0, 0);
    }

    setCreatingBooking(true);
    try {
      const booking = await ClientBookingService.createBooking({
        userId: user.id,
        routeId: selectedRoute.id,
        passengerName: bookingForm.passengerName || fullName,
        passengerPhone: bookingForm.passengerPhone || user.phoneNumber || '',
        passengerEmail: bookingForm.passengerEmail || user.email,
        seatNumber: bookingForm.seatNumber || 'Auto assign',
        travelDate,
        totalAmount: selectedRoute.price || 0,
        notes: bookingForm.notes,
      });

      setBookings((current) => [booking, ...current]);
      setBookingForm((current) => ({ ...current, routeId: '', travelDate: '', seatNumber: '', notes: '' }));
      setActiveTab('bookings');
      toast.success('Trip booked. Payment is pending.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to book trip');
    } finally {
      setCreatingBooking(false);
    }
  };

  const toggleSavedRoute = (routeId: string) => {
    setSavedRouteIds((current) =>
      current.includes(routeId) ? current.filter((id) => id !== routeId) : [...current, routeId]
    );
  };

  const handleHubtelPayment = async (booking: Booking) => {
    if (!booking) return;
    setInitializingPayment(true);
    try {
      const response = await fetch('https://us-central1-wote-intercity.cloudfunctions.net/initializeHubtelPayment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: booking.totalAmount,
          bookingId: booking.id,
          bookingReference: booking.bookingReference,
        }),
      });

      if (!response.ok) throw new Error('Payment initialization failed');
      const data = await response.json();
      
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to start payment');
    } finally {
      setInitializingPayment(false);
    }
  };

  const renderBookingList = (compact = false) => {
    if (bookings.length === 0) {
      return (
        <div className="text-center py-12 border-2 border-dashed border-secondary-200 rounded-lg">
          <Ticket className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-secondary-900">No bookings yet</h3>
          <p className="text-secondary-500 mb-5">You have not booked any trips with us yet.</p>
          <button
            type="button"
            onClick={() => setActiveTab('book')}
            className="bg-brand-orange text-white px-6 py-2.5 rounded-full font-semibold hover:bg-brand-orange/90 transition-colors"
          >
            Book a Trip
          </button>
        </div>
      );
    }

    const visibleBookings = compact ? bookings.slice(0, 3) : bookings;

    return (
      <div className="divide-y divide-secondary-100 border border-secondary-100 rounded-lg overflow-hidden">
        {visibleBookings.map((booking) => (
          <div key={booking.id} className="bg-white p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h3 className="text-lg font-bold text-secondary-900">
                  {booking.route?.fromCity || 'Route'} <span className="text-secondary-400">to</span> {booking.route?.toCity || 'destination'}
                </h3>
                <span className={`px-2.5 py-1 rounded-full border text-xs font-bold uppercase ${statusClass(booking.bookingStatus)}`}>
                  {booking.bookingStatus}
                </span>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-secondary-600">
                <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{formatDate(booking.travelDate)}</span>
                <span className="flex items-center gap-1.5"><Ticket className="w-4 h-4" />{booking.bookingReference}</span>
                <span className="flex items-center gap-1.5"><CreditCard className="w-4 h-4" />{formatMoney(booking.totalAmount)}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full border text-xs font-bold uppercase ${statusClass(booking.paymentStatus)}`}>
                {booking.paymentStatus}
              </span>
              <button
                type="button"
                className="text-sm font-semibold text-brand-blue hover:text-brand-orange transition-colors"
                onClick={() => setSelectedBooking(booking)}
              >
                Details
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-brand-blue animate-spin mx-auto mb-3" />
          <p className="text-secondary-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row gap-8">
          <aside className="w-full md:w-72 shrink-0">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-secondary-100 mb-5 text-center">
              <div className="w-20 h-20 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-10 h-10 text-brand-blue" />
              </div>
              <h2 className="text-lg font-bold text-secondary-900 break-words">{fullName}</h2>
              <p className="text-sm text-secondary-500 break-words">{user?.email}</p>
            </div>

            <nav className="bg-white rounded-lg border border-secondary-100 p-2 shadow-sm">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-semibold transition-colors ${
                      active
                        ? 'bg-brand-blue text-white'
                        : 'text-secondary-600 hover:bg-secondary-50 hover:text-brand-blue'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                );
              })}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-danger-600 hover:bg-danger-50 rounded-lg font-semibold transition-colors mt-4"
              >
                <LogOut className="w-5 h-5" />
                Log Out
              </button>
            </nav>
          </aside>

          <main className="flex-1 space-y-8">
            <motion.section
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="bg-white p-6 sm:p-8 rounded-lg shadow-sm border border-secondary-100"
            >
              {activeTab === 'profile' && (
                <div className="space-y-8">
                  <div>
                    <h1 className="text-2xl font-bold text-brand-blue mb-2">My Profile</h1>
                    <p className="text-secondary-600">Manage the personal details used for your bookings.</p>
                  </div>

                  <form onSubmit={handleProfileSave} className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <label className="block">
                      <span className="text-sm font-semibold text-secondary-700">First name</span>
                      <input
                        value={profileForm.firstName}
                        onChange={(event) => setProfileForm({ ...profileForm, firstName: event.target.value })}
                        className="mt-2 w-full rounded-lg border border-secondary-200 px-4 py-3 focus:border-brand-blue focus:ring-brand-blue"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-semibold text-secondary-700">Last name</span>
                      <input
                        value={profileForm.lastName}
                        onChange={(event) => setProfileForm({ ...profileForm, lastName: event.target.value })}
                        className="mt-2 w-full rounded-lg border border-secondary-200 px-4 py-3 focus:border-brand-blue focus:ring-brand-blue"
                      />
                    </label>
                    <label className="block md:col-span-2">
                      <span className="text-sm font-semibold text-secondary-700">Phone number</span>
                      <div className="relative mt-2">
                        <Phone className="absolute left-3 top-3.5 w-5 h-5 text-secondary-400" />
                        <input
                          value={profileForm.phoneNumber}
                          onChange={(event) => setProfileForm({ ...profileForm, phoneNumber: event.target.value })}
                          className="w-full rounded-lg border border-secondary-200 pl-11 pr-4 py-3 focus:border-brand-blue focus:ring-brand-blue"
                          placeholder="+233..."
                        />
                      </div>
                    </label>
                    <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-secondary-100 pt-5">
                      <div className="flex items-center gap-2 text-sm text-secondary-600">
                        <Mail className="w-4 h-4" />
                        {user?.email}
                      </div>
                      <button
                        type="submit"
                        disabled={savingProfile}
                        className="inline-flex items-center justify-center gap-2 bg-brand-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-blue/90 disabled:opacity-60"
                      >
                        {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save Profile
                      </button>
                    </div>
                  </form>

                  <div className="bg-brand-orange/10 border border-brand-orange/20 rounded-lg p-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                      <div>
                        <h3 className="text-xl font-bold text-brand-orange mb-2 flex items-center gap-2">
                          <Download className="w-6 h-6" />
                          Get the Mobile App
                        </h3>
                        <p className="text-secondary-700">
                          Book faster, track buses in real time, and manage trips from your phone.
                        </p>
                      </div>
                      <div className="flex flex-col gap-3 min-w-[200px] w-full sm:w-auto">
                        <a href="#" className="bg-secondary-900 text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-black transition-colors">
                          App Store
                        </a>
                        <a href="#" className="bg-brand-blue text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-brand-blue/90 transition-colors">
                          Google Play
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'bookings' && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h1 className="text-2xl font-bold text-brand-blue mb-2">My Bookings</h1>
                      <p className="text-secondary-600">View your trip history and booking payment status.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveTab('book')}
                      className="bg-brand-orange text-white px-5 py-3 rounded-lg font-semibold hover:bg-brand-orange/90 transition-colors"
                    >
                      Book a Trip
                    </button>
                  </div>
                  {renderBookingList(false)}
                </div>
              )}

              {activeTab === 'book' && (
                <form onSubmit={handleCreateBooking} className="space-y-8">
                  <div>
                    <h1 className="text-2xl font-bold text-brand-blue mb-2">Book a Trip</h1>
                    <p className="text-secondary-600">Choose an active route, confirm passenger details, and reserve your seat.</p>
                  </div>

                  {routes.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-secondary-200 rounded-lg">
                      <AlertCircle className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
                      <p className="text-secondary-600 font-medium">No active routes are available right now.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {routes.map((route) => {
                        const active = bookingForm.routeId === route.id;
                        return (
                          <button
                            type="button"
                            key={route.id}
                            onClick={() => handleRouteSelect(route.id)}
                            className={`text-left p-5 rounded-lg border transition-all ${
                              active
                                ? 'border-brand-blue bg-brand-blue/5 shadow-sm'
                                : 'border-secondary-200 hover:border-brand-blue/40 hover:bg-secondary-50'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h3 className="font-bold text-secondary-900 text-lg">
                                  {route.fromCity} <span className="text-secondary-400">to</span> {route.toCity}
                                </h3>
                                <div className="mt-2 flex flex-wrap gap-3 text-sm text-secondary-600">
                                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{Math.floor(route.duration / 60)}h {route.duration % 60}m</span>
                                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{route.distance || 0} km</span>
                                  <span className="font-semibold text-brand-blue">{formatMoney(route.price)}</span>
                                </div>
                              </div>
                              {active && <CheckCircle2 className="w-6 h-6 text-brand-blue shrink-0" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-secondary-100 pt-6">
                    <label className="block">
                      <span className="text-sm font-semibold text-secondary-700">Travel date</span>
                      <input
                        type="date"
                        value={bookingForm.travelDate}
                        min={new Date().toISOString().slice(0, 10)}
                        onChange={(event) => setBookingForm({ ...bookingForm, travelDate: event.target.value })}
                        className="mt-2 w-full rounded-lg border border-secondary-200 px-4 py-3 focus:border-brand-blue focus:ring-brand-blue"
                      />
                      {routeSchedules.length > 0 && (
                        <p className="mt-2 text-xs text-secondary-500">
                          Next route departure: {formatDate(routeSchedules[0].departureTime)}
                        </p>
                      )}
                    </label>
                    <label className="block">
                      <span className="text-sm font-semibold text-secondary-700">Preferred seat</span>
                      <input
                        value={bookingForm.seatNumber}
                        onChange={(event) => setBookingForm({ ...bookingForm, seatNumber: event.target.value })}
                        className="mt-2 w-full rounded-lg border border-secondary-200 px-4 py-3 focus:border-brand-blue focus:ring-brand-blue"
                        placeholder="Auto assign"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-semibold text-secondary-700">Passenger name</span>
                      <input
                        value={bookingForm.passengerName}
                        onChange={(event) => setBookingForm({ ...bookingForm, passengerName: event.target.value })}
                        className="mt-2 w-full rounded-lg border border-secondary-200 px-4 py-3 focus:border-brand-blue focus:ring-brand-blue"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-semibold text-secondary-700">Passenger phone</span>
                      <input
                        value={bookingForm.passengerPhone}
                        onChange={(event) => setBookingForm({ ...bookingForm, passengerPhone: event.target.value })}
                        className="mt-2 w-full rounded-lg border border-secondary-200 px-4 py-3 focus:border-brand-blue focus:ring-brand-blue"
                      />
                    </label>
                    <label className="block md:col-span-2">
                      <span className="text-sm font-semibold text-secondary-700">Notes</span>
                      <textarea
                        value={bookingForm.notes}
                        onChange={(event) => setBookingForm({ ...bookingForm, notes: event.target.value })}
                        className="mt-2 w-full rounded-lg border border-secondary-200 px-4 py-3 focus:border-brand-blue focus:ring-brand-blue"
                        rows={3}
                        placeholder="Optional luggage or pickup notes"
                      />
                    </label>
                  </div>

                  <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="text-sm text-secondary-500">Trip total</p>
                      <p className="text-3xl font-extrabold text-brand-blue">{formatMoney(selectedRoute?.price)}</p>
                      <p className="text-xs text-secondary-500">Payment status will be marked pending after booking.</p>
                    </div>
                    <button
                      type="submit"
                      disabled={creatingBooking || !selectedRoute}
                      className="inline-flex items-center justify-center gap-2 bg-brand-orange text-white px-7 py-3 rounded-lg font-bold hover:bg-brand-orange/90 disabled:opacity-60"
                    >
                      {creatingBooking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />}
                      Confirm Booking
                    </button>
                  </div>
                </form>
              )}

              {activeTab === 'saved' && (
                <div className="space-y-6">
                  <div>
                    <h1 className="text-2xl font-bold text-brand-blue mb-2">Saved Routes</h1>
                    <p className="text-secondary-600">Keep frequent routes ready for quick booking.</p>
                  </div>

                  {savedRoutes.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-secondary-200 rounded-lg">
                      <Heart className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-secondary-900">No saved routes</h3>
                      <p className="text-secondary-500">Save a route from the list below.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {savedRoutes.map((route) => (
                        <div key={route.id} className="border border-secondary-200 rounded-lg p-5">
                          <h3 className="font-bold text-secondary-900">{route.fromCity} to {route.toCity}</h3>
                          <p className="text-sm text-secondary-500 mt-1">{formatMoney(route.price)} base fare</p>
                          <button
                            type="button"
                            onClick={() => {
                              handleRouteSelect(route.id);
                              setActiveTab('book');
                            }}
                            className="mt-4 text-sm font-semibold text-brand-blue hover:text-brand-orange"
                          >
                            Book this route
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="border-t border-secondary-100 pt-6">
                    <h2 className="text-lg font-bold text-secondary-900 mb-4">Available routes</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {routes.map((route) => {
                        const saved = savedRouteIds.includes(route.id);
                        return (
                          <button
                            key={route.id}
                            type="button"
                            onClick={() => toggleSavedRoute(route.id)}
                            className="text-left border border-secondary-200 rounded-lg p-4 hover:border-brand-blue/40 transition-colors"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <p className="font-bold text-secondary-900">{route.fromCity} to {route.toCity}</p>
                                <p className="text-sm text-secondary-500">{formatMoney(route.price)}</p>
                              </div>
                              <Heart className={`w-5 h-5 ${saved ? 'fill-brand-orange text-brand-orange' : 'text-secondary-400'}`} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="space-y-8">
                  <div>
                    <h1 className="text-2xl font-bold text-brand-blue mb-2">Settings</h1>
                    <p className="text-secondary-600">Manage account security and trip notifications.</p>
                  </div>

                  <div className="space-y-4">
                    <h2 className="text-lg font-bold text-secondary-900 flex items-center gap-2">
                      <Bell className="w-5 h-5 text-brand-orange" />
                      Notifications
                    </h2>
                    {[
                      ['email', 'Email updates', 'Booking confirmations and receipts.'],
                      ['sms', 'SMS alerts', 'Departure reminders and service updates.'],
                      ['push', 'Push notifications', 'Mobile app route and payment updates.'],
                    ].map(([key, title, description]) => (
                      <label key={key} className="flex items-center justify-between gap-5 border border-secondary-200 rounded-lg p-4">
                        <span>
                          <span className="block font-semibold text-secondary-900">{title}</span>
                          <span className="block text-sm text-secondary-500">{description}</span>
                        </span>
                        <input
                          type="checkbox"
                          checked={prefs[key as keyof typeof prefs]}
                          onChange={(event) => setPrefs({ ...prefs, [key]: event.target.checked })}
                          className="h-5 w-5 rounded border-secondary-300 text-brand-blue focus:ring-brand-blue"
                        />
                      </label>
                    ))}
                    <button
                      type="button"
                      onClick={handlePrefsSave}
                      disabled={savingPrefs}
                      className="inline-flex items-center justify-center gap-2 bg-brand-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-blue/90 disabled:opacity-60"
                    >
                      {savingPrefs ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Settings
                    </button>
                  </div>

                  <form onSubmit={handlePasswordSave} className="border-t border-secondary-100 pt-6 space-y-5">
                    <h2 className="text-lg font-bold text-secondary-900 flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-brand-orange" />
                      Change Password
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <input
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, currentPassword: event.target.value })}
                        className="rounded-lg border border-secondary-200 px-4 py-3 focus:border-brand-blue focus:ring-brand-blue"
                        placeholder="Current password"
                      />
                      <input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })}
                        className="rounded-lg border border-secondary-200 px-4 py-3 focus:border-brand-blue focus:ring-brand-blue"
                        placeholder="New password"
                      />
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })}
                        className="rounded-lg border border-secondary-200 px-4 py-3 focus:border-brand-blue focus:ring-brand-blue"
                        placeholder="Confirm password"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={savingPassword}
                      className="inline-flex items-center justify-center gap-2 bg-secondary-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-black disabled:opacity-60"
                    >
                      {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                      Update Password
                    </button>
                  </form>
                </div>
              )}
            </motion.section>

            {activeTab === 'profile' && (
              <motion.section
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 }}
                className="bg-white p-6 sm:p-8 rounded-lg shadow-sm border border-secondary-100"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                  <div>
                    <h2 className="text-xl font-bold text-brand-blue">Recent Bookings</h2>
                    <p className="text-secondary-500 text-sm">Your latest trip activity.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab('bookings')}
                    className="text-sm font-semibold text-brand-blue hover:text-brand-orange"
                  >
                    View all
                  </button>
                </div>
                {renderBookingList(true)}
              </motion.section>
            )}
          </main>
        </div>
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
          >
            <div className="flex items-center justify-between p-5 border-b border-secondary-100 bg-secondary-50">
              <h2 className="text-xl font-bold text-brand-blue flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                {selectedBooking.paymentStatus === 'paid' ? 'Ticket Details' : 'Reservation Details'}
              </h2>
              <button
                onClick={() => setSelectedBooking(null)}
                className="p-2 hover:bg-secondary-200 rounded-full text-secondary-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <p className="text-sm text-secondary-500 uppercase font-bold tracking-wider mb-1">Trip Reference</p>
                  <p className="text-xl font-bold text-secondary-900">{selectedBooking.bookingReference}</p>
                </div>
                <div className={`px-4 py-1.5 rounded-full border text-sm font-bold uppercase ${statusClass(selectedBooking.paymentStatus)}`}>
                  {selectedBooking.paymentStatus === 'paid' ? 'Paid' : 'Unpaid Reservation'}
                </div>
              </div>

              <div className="bg-secondary-50 border border-secondary-100 rounded-xl p-5 mb-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-secondary-500 mb-1 flex items-center gap-1"><MapPin className="w-4 h-4"/> From</p>
                    <p className="font-bold text-secondary-900">{selectedBooking.route?.fromCity || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary-500 mb-1 flex items-center gap-1"><MapPin className="w-4 h-4"/> To</p>
                    <p className="font-bold text-secondary-900">{selectedBooking.route?.toCity || 'N/A'}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-secondary-200">
                  <div>
                    <p className="text-sm text-secondary-500 mb-1 flex items-center gap-1"><CalendarDays className="w-4 h-4"/> Travel Date</p>
                    <p className="font-bold text-secondary-900">{formatDate(selectedBooking.travelDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary-500 mb-1 flex items-center gap-1"><Clock className="w-4 h-4"/> Departure Time</p>
                    <p className="font-bold text-secondary-900">{selectedBooking.route?.departureTime || 'TBD'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-secondary-200">
                  <div>
                    <p className="text-sm text-secondary-500 mb-1 flex items-center gap-1"><User className="w-4 h-4"/> Passenger</p>
                    <p className="font-bold text-secondary-900">{selectedBooking.passengerName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary-500 mb-1 flex items-center gap-1"><Bus className="w-4 h-4"/> Seat Number</p>
                    <p className="font-bold text-secondary-900">{selectedBooking.seatNumber}</p>
                  </div>
                </div>
              </div>

              {selectedBooking.paymentStatus === 'paid' ? (
                <div className="flex flex-col items-center justify-center p-6 bg-white border border-secondary-200 rounded-xl shadow-sm text-center">
                  <p className="text-sm font-semibold text-success-600 mb-4 uppercase tracking-widest flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" /> Boarding Pass Ready
                  </p>
                  <div className="p-3 bg-white border-2 border-secondary-100 rounded-xl">
                    <QRCodeSVG 
                      value={`WOTE-TICKET:${selectedBooking.bookingReference}:${selectedBooking.id}`} 
                      size={180} 
                      level="Q"
                      includeMargin={true}
                    />
                  </div>
                  <p className="text-xs text-secondary-400 mt-4">Show this QR code when boarding</p>
                </div>
              ) : (
                <div className="bg-brand-blue/5 border border-brand-blue/20 rounded-xl p-5 text-center">
                  <p className="text-brand-blue font-bold text-lg mb-2">Total: {formatMoney(selectedBooking.totalAmount)}</p>
                  <p className="text-sm text-secondary-600 mb-5">Your reservation is held. Secure your seat by paying now via Hubtel.</p>
                  <button
                    onClick={() => handleHubtelPayment(selectedBooking)}
                    disabled={initializingPayment}
                    className="w-full flex items-center justify-center gap-2 bg-brand-orange text-white px-6 py-3.5 rounded-lg font-bold hover:bg-brand-orange/90 disabled:opacity-70 transition-colors"
                  >
                    {initializingPayment ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                    Pay with Hubtel
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
